/**
 * TypeScript types for the kyykka-infra frontend.
 * 
 * These types match the Django REST Framework serializers in the backend.
 */

// ============================================================================
// Auth Types
// ============================================================================

export interface LoginRequest {
  username: string;
  password: string;
}

export interface TokenResponse {
  access: string;
  refresh: string;
}

export interface User {
  id: number;
  uuid?: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  telegram: string;
  referee_years: number;
  is_staff: boolean;
  is_active: boolean;
}

// ============================================================================
// Team Types
// ============================================================================

export interface Team {
  id: number;
  team_name: string;
  league_id: string;
  variant: string;
  store_id: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  contact_members: string;
  team_luck: number;
  disqualify_round: number | null;
  is_disqualified: boolean;
  start_paper_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface TeamComment {
  id: number;
  team: number;
  comment: string;
  created_by_username: string | null;
  created_at: string;
}

// ============================================================================
// Game Types
// ============================================================================

export type RoundName = 
  | 'start_block'
  | 'continuation_block'
  | 'quarter_final'
  | 'semi_final'
  | 'bronze_game'
  | 'final';

export type EventType =
  | 'game_start'
  | 'game_1st'
  | 'game_2nd'
  | 'game_ot'
  | 'game_end'
  | 'game_cancel'
  | 'game_reset'
  | 'game_reopen';

export type GameStatus = 'scheduled' | 'in_progress' | 'finished' | 'cancelled';

export interface Game {
  id: number;
  paper_id: number;
  league_id: string;
  round: number;
  round_name: RoundName | null;
  
  // State flags
  game_start: boolean;
  game_1st: boolean;
  game_2nd: boolean;
  game_ot: boolean;
  game_end: boolean;
  game_cancel: boolean;
  
  // Scheduling
  schedule_start: string;
  schedule_end: string;
  time_start: string | null;
  time_end: string | null;
  
  // Court/Field
  court_id: number | null;
  
  // Teams (IDs from serializer)
  team1: number | null;
  team2: number | null;
  team1_name: string | null;
  team2_name: string | null;
  
  // Scores by half
  team1_1st_score: number | null;
  team2_1st_score: number | null;
  team1_2nd_score: number | null;
  team2_2nd_score: number | null;
  team1_ot_score: number | null;
  team2_ot_score: number | null;
  
  // Computed totals
  team1_score: number | null;
  team2_score: number | null;
  winner_id: number | null;
  is_tie: boolean | null;
  
  // Referee info
  referee: number | null;
  referee_name: string | null;
  referee_phone: string | null;
  referee_telegram: string | null;
}

/**
 * Get the display status of a game.
 */
export function getGameStatus(game: Game): GameStatus {
  if (game.game_cancel) return 'cancelled';
  if (game.game_end) return 'finished';
  if (game.game_start) return 'in_progress';
  return 'scheduled';
}

// ============================================================================
// Referee Types
// ============================================================================

/**
 * Referee status enum values (integers matching backend Referee.Status).
 */
export type RefereeStatusValue = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

/**
 * League filter enum for referee game assignments.
 */
export type LeagueFilter = 'all' | 'yle' | 'yle09' | 'yle12' | 'pro' | 'frm';

export interface Referee {
  id: number;
  user: User;
  field: string | null;
  field_group: number | null;
  field_group_name: string | null;
  is_area_referee: boolean;
  status: RefereeStatusValue;
  status_display: string;
  comment: string;
  deposit: number;
  deposit_display: string;
  reward_given: number;
  reward_display: string;
  league_filter: LeagueFilter;
  league_filter_display: string;
  shifts: RefereeShift[];
  created_at: string;
  updated_at: string;
}

export interface RefereeShift {
  id: number;
  referee: number;
  date: string;
  start_time: string;
  end_time: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Field Types
// ============================================================================

export interface Field {
  name: string;  // Primary key (e.g., "1", "A1", "PRO1")
  latitude: number;
  longitude: number;
  fieldgroup: string | null;
  created_at: string;
  updated_at: string;
}

export interface FieldGroup {
  id: number;
  name: string;
  svg_name: string;
  fields: string[];  // List of field names
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Admin Referee Management Types
// ============================================================================

export interface SeedFieldsRequest {
  court_count?: number;
  groups?: string[];
}

export interface SeedFieldsResponse {
  status: 'ok' | 'fail';
  message: string;
  field_groups_created: number;
  fields_created: number;
}

export interface GenerateRefereesRequest {
  count?: number;
  seed?: number;
}

export interface GenerateRefereesResponse {
  status: 'ok' | 'fail';
  message: string;
  users_created: number;
  referees_created: number;
}

export interface ClearGeneratedRefereesResponse {
  status: 'ok' | 'fail';
  message: string;
  deleted: {
    referees: number;
    users: number;
  };
}

export interface AssignCourtsRequest {
  league_filter?: LeagueFilter;
}

export interface AssignCourtsResponse {
  status: 'ok' | 'fail';
  message: string;
  referees_assigned: number;
  games_assigned: number;
}

// ============================================================================
// Event Submission Types
// ============================================================================

export interface EventSubmitRequest {
  paper_id: string;
  team1_id: number;
  team2_id: number;
  event: EventType;
  team1_score?: number;
  team2_score?: number;
}

export interface EventSubmitResponse {
  status: 'ok' | 'fail';
  message: string;
  game_id?: number;
}

// ============================================================================
// Live API Types
// ============================================================================

export interface LiveGames {
  ongoing: Game[];
  upcoming: Game[];
  finished: Game[];
}

export interface TeamStanding {
  team_id: number;
  team_name: string;
  games_played: number;
  wins: number;
  ties: number;
  losses: number;
  team_points: number;
  average: number;
  is_disqualified?: boolean;
}

export interface RefereeInfo {
  id: number;
  username: string;
  name: string;
  phone: string;
  status: string;
}

export interface BlockData {
  games: LiveGames;
  points: TeamStanding[];
  referee?: RefereeInfo | null;
  pathway?: PathwayStep[];
}

export interface LeagueData {
  games: LiveGames;
}

// ============================================================================
// Media Asset Types
// ============================================================================

export type MediaAssetType = 'club-logo' | 'kyykka-logo' | 'advertisement' | 'sidebar-logo';

export interface MediaAsset {
  id: number;
  name: string;
  image_type: MediaAssetType;
  image_type_display: string;
  file: string;
  file_url: string;
  active: boolean;
  order: number;
  created_at: string;
  updated_at: string;
}

export interface PrintAssets {
  club_logos: MediaAsset[];
  kyykka_logos: MediaAsset[];
  advertisements: MediaAsset[];
  sidebar_logos: MediaAsset[];
}

// ============================================================================
// Contact Types
// ============================================================================

export type ContactRole = 'paatuomari-firma' | 'paatuomari-pro' | 'paatuomari-yle' | 'tulospalvelu';

export interface Contact {
  id: number;
  name: string;
  phone: string;
  role: ContactRole;
  role_display: string;
  order: number;
  active: boolean;
}

export interface PrintContacts {
  paatuomari_firma: Contact[];
  paatuomari_pro: Contact[];
  paatuomari_yle: Contact[];
  tulospalvelu: Contact[];
}

// ============================================================================
// Venue Map Overlay Types
// ============================================================================

export interface VenueMapOverlay {
  id: number;
  name: string;
  file: string;
  file_url: string;
  top_left_lat: string;
  top_left_lng: string;
  delta_x: string;
  delta_y: string;
  rotation: string;
  center_lat: string;
  center_lng: string;
  default_zoom: number;
  active: boolean;
  order: number;
  created_at: string;
  updated_at: string;
}

export interface MapConfig {
  file_url: string;
  top_left_lat: string;
  top_left_lng: string;
  delta_x: string;
  delta_y: string;
  rotation: string;
  center_lat: string;
  center_lng: string;
  default_zoom: number;
}

// ============================================================================
// Head Judge Log Types
// ============================================================================

export type HeadJudgeLogStatus = 'new' | 'in_progress' | 'done';

export interface HeadJudgeLogEntry {
  id: number;
  field: string | null;  // Field.name is the PK (string)
  field_name: string | null;
  description: string;
  status: HeadJudgeLogStatus;
  status_display: string;
  assigned_to: number | null;
  assigned_to_name: string | null;
  created_by: number;
  created_by_name: string;
  created_at: string;
  updated_at: string;
}

export interface HeadJudgeLogCounts {
  new: number;
  in_progress: number;
  done: number;
  total: number;
}

export interface AdminUser {
  id: number;
  name: string;
}

// ============================================================================
// Print Types
// ============================================================================

export interface PrintGame {
  id: number;
  paper_id?: number;
  court_id: number | null;
  schedule_start: string;
  schedule_end: string;
  team1_id: number | null;
  team1_name: string;
  team2_id: number | null;
  team2_name: string;
  // Judge paper specific
  team1_1st_score?: number | null;
  team2_1st_score?: number | null;
  team1_2nd_score?: number | null;
  team2_2nd_score?: number | null;
  team1_score?: number | null;
  team2_score?: number | null;
  game_ot?: boolean;
  team1_ot_score?: number | null;
  team2_ot_score?: number | null;
  winner_id?: number | null;
  winner_name?: string;
}

export interface PathwayStep {
  paper_id: number;
  court_id: number | null;
  schedule_start: string;
  schedule_end: string;
}

export interface TeamPrintData {
  id: number;
  team_name: string;
  league_id: string;
  variant: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  store_id: string;
  start_paper_id: number | null;
  paper_id_display: string;
  games: PrintGame[];
  pathway: PathwayStep[];
  qr_code: string;  // Base64 data URL
}

export interface JudgePrintData {
  paper_id: number;
  paper_id_display: string;
  league_id: string;
  round: number;
  court_id: number | null;
  schedule_start: string;
  schedule_end: string;
  games: PrintGame[];
  pathway: PathwayStep[];
  qr_code: string;  // Base64 data URL
}

// ============================================================================
// Court Preferences Types
// ============================================================================

export interface CourtPreference {
  id: number;
  team: number;
  team_name: string;
  variant: string;
  court_number: number;
}

export interface CreateCourtPreferenceRequest {
  team: number;
  court_number: number;
}

// ============================================================================
// Tournament Overview Types (TiKe)
// ============================================================================

export type OverviewGameStatus = 'scheduled' | 'started' | 'finished' | 'late' | 'cancelled';

export interface OverviewGame {
  game_id: number;
  paper_id: number;
  league_id: string;
  round: number;
  status: OverviewGameStatus;
  display: string;  // Time or minute diff
  team1_name: string | null;
  team2_name: string | null;
}

export interface OverviewBlock {
  paper_id: number;
  league_id: string;
  games?: OverviewGame[];  // Only in games view
  // Stats for blocks/rounds view
  round?: number;
  total?: number;
  scheduled?: number;
  started?: number;
  finished?: number;
  late?: number;
  cancelled?: number;
}

export interface OverviewCourt {
  court_id: number;
  blocks: OverviewBlock[];
}

export interface OverviewRound {
  league_id: string;
  round: number;
  total: number;
  scheduled: number;
  started: number;
  finished: number;
  late: number;
  cancelled: number;
}

export type TournamentOverviewResponse = 
  | { group: 'games'; courts: OverviewCourt[] }
  | { group: 'blocks'; courts: OverviewCourt[] }
  | { group: 'rounds'; rounds: OverviewRound[] };

// ============================================================================
// Admin API Types
// ============================================================================

export interface GenerateTournamentRequest {
  mode: 'games' | 'synthetic' | 'kide';
  clear_existing?: boolean;
  // Synthetic mode options
  yle09_count?: number;
  yle12_count?: number;
  pro_count?: number;
  seed?: number;
  // Kide mode options
  kide_data?: Record<string, unknown>;
}

export interface GenerateTournamentResponse {
  status: 'ok' | 'fail';
  message: string;
  teams_created: number;
  games_created: number;
  items_created: number;
  summary: {
    yle_teams: number;
    pro_teams: number;
    frm_teams: number;
    yle_games: number;
    pro_games: number;
    frm_games: number;
  };
}

export interface TournamentStats {
  teams: {
    total: number;
    yle: number;
    pro: number;
    frm: number;
  };
  games: {
    total: number;
    yle: number;
    pro: number;
    frm: number;
  };
  games_by_status: {
    scheduled: number;
    in_progress: number;
    finished: number;
    cancelled: number;
  };
}

export interface DisqualifyResponse {
  status: 'ok' | 'fail';
  message: string;
  team_id?: number;
  disqualify_round?: number;
}

export interface UndisqualifyResponse {
  status: 'ok' | 'fail';
  message: string;
  team_id?: number;
  team_name?: string;
  previous_dq_round?: number;
  games_restored?: number;
  blocks_affected?: number[];
  propagation_undone?: boolean;
}

export interface ClearTournamentResponse {
  status: 'ok' | 'fail';
  message: string;
  deleted: {
    games: number;
    scores: number;
    events: number;
    teams: number;
    items: number;
    game_queue: number;
    message_queue: number;
  };
}

export interface ClearUsersResponse {
  status: 'ok' | 'fail';
  message: string;
  deleted: {
    users: number;
    referees: number;
  };
}

export interface ClearFieldsResponse {
  status: 'ok' | 'fail';
  message: string;
  fields_deleted: number;
  field_groups_deleted: number;
}

export interface ClearAuditLogResponse {
  status: 'ok' | 'fail';
  message: string;
  deleted: {
    audit_logs: number;
  };
}

// ============================================================================
// Simulation Types
// ============================================================================

export interface SimulateRequest {
  mode: 'block' | 'round' | 'league';
  league?: string;
  round?: number;
  block_id?: number;
}

export interface SimulateResponse {
  status: 'ok' | 'fail';
  mode: 'block' | 'round' | 'league';
  league?: string;
  round?: number;
  block_id?: number;
  games_played: number;
  games_with_overtime: number;
  final_winner?: string | null;
  bronze_winner?: string | null;
  error?: string;
}

// ============================================================================
// Courts Summary Types
// ============================================================================

export interface CourtReferee {
  id: number;
  first_name: string;
  last_name: string;
  status: number;
  status_display: string;
  league_filter: LeagueFilter;
  shift_time: string | null;
}

export interface CourtFieldInfo {
  name: string;
  fieldgroup: string | null;
  latitude: number;
  longitude: number;
}

export interface CourtSummary {
  court_id: number;
  field: CourtFieldInfo | null;
  referees: CourtReferee[];
  games_total: number;
  games_finished: number;
  games_in_progress: number;
  games_scheduled: number;
  first_game_start: string | null;
  last_game_end: string | null;
}

export interface CourtsSummaryResponse {
  courts: CourtSummary[];
}

// ============================================================================
// Games List Params
// ============================================================================

export interface GameListParams {
  league?: string;
  round?: number;
  paper?: number;
  court?: number;
  team?: number;
  status?: GameStatus;
}

// ============================================================================
// Judge Types (TISSI Interface)
// ============================================================================

/**
 * Game in the judge's queue with full details.
 */
export interface JudgeQueueGame {
  id: number;
  game: number;
  paper_id: number;
  league_id: string;
  round: number;
  court_id: number | null;
  schedule_start: string;
  schedule_end: string;
  game_start: boolean;
  game_end: boolean;
  game_cancel: boolean;
  team1_id: number | null;
  team1_name: string | null;
  team2_id: number | null;
  team2_name: string | null;
  automatic: boolean;
  created_at: string;
}

/**
 * Team for autocomplete in judge interface.
 */
export interface JudgeTeam {
  id: number;
  name: string;
}

/**
 * Message for judge from admin.
 */
export interface JudgeMessage {
  id: number;
  message: string;
  status: 'ok' | 'fail' | 'info';
  visible: boolean;
  timestamp: string;
}

// ============================================================================
// Tournament Timetable Types
// ============================================================================

export interface LeagueRound {
  id: number;
  league: 'yle' | 'pro' | 'frm';
  round_number: number;
  round_name: string;
  courts: number[];
  time_slots: Array<[string, string]>; // [[start, end], ...]
  created_at: string;
  updated_at: string;
}

export type LeagueRoundUpdateRequest = Partial<
  Pick<LeagueRound, 'round_name' | 'courts' | 'time_slots'>
>;

export interface Tournament {
  year: number;
  date: string;
  rounds: LeagueRound[];
  round_count: number;
  created_at: string;
  updated_at: string;
}

export interface TournamentListItem {
  year: number;
  date: string;
  round_count: number;
}

export interface TournamentCopyRequest {
  target_year: number;
  target_date: string;
}

export interface TournamentCopyResponse {
  message: string;
  year: number;
  rounds_copied: number;
}

export interface TournamentImportRequest {
  data: Record<string, unknown>;
  overwrite?: boolean;
}

export interface TournamentImportResponse {
  message: string;
  year: number;
  rounds_imported: number;
}

// ============================================================================
// Audit Log Types
// ============================================================================

export interface AuditLog {
  id: number;
  created_at: string;
  user: number | null;
  user_display: string | null;
  entity_type: 'game' | 'team' | 'block' | 'court' | 'referee';
  entity_type_display: string;
  action: string;
  action_display: string;
  description: string;
  data: Record<string, unknown>;
  game_id: number | null;
  team_id: number | null;
  block_id: number | null;
  court_id: number | null;
  referee_id: number | null;
}

export interface AuditLogParams {
  game_id?: number;
  team_id?: number;
  block_id?: number;
  court_id?: number;
  referee_id?: number;
  entity_type?: string;
  action?: string;
}

// ============================================================================
// Analytics Types
// ============================================================================

export interface AnalyticsSummary {
  total_sessions: number;
  unique_visitors: number;
  total_page_views: number;
  bounce_rate: number;
  return_visitors: number;
  avg_duration_seconds: number;
  sessions_by_hour: Array<{
    hour: string;
    count: number;
  }>;
  time_range_hours: number;
}

export interface AnalyticsPageStats {
  pages: Array<{
    page_path: string;
    visits: number;
    unique_sessions: number;
  }>;
  time_range_hours: number;
}

export interface AnalyticsFlows {
  flows: Array<{
    referrer: string;
    page_path: string;
    count: number;
  }>;
  entry_pages: Array<{
    page_path: string;
    count: number;
  }>;
  time_range_hours: number;
}

export interface AnalyticsJourneys {
  journeys: Array<{
    session_id: string;
    visitor_id: string;
    started_at: string;
    page_count: number;
    pages: string[];
  }>;
  time_range_hours: number;
}

export interface AnalyticsRoutes {
  routes: Array<{
    page_path: string;
    visits: number;
    unique_sessions: number;
  }>;
  time_range_hours: number;
}

export interface AnalyticsPatterns {
  patterns: Array<{
    pattern: string;
    count: number;
    percentage: number;
  }>;
  total_sessions: number;
  time_range_hours: number;
}

// ============================================================================
// Kyykkä Scoring Constants and Utilities
// ============================================================================

/**
 * Kyykkä scoring constants.
 * - Each team has 20 torni (towers of 2 kyykät) = 40 kyykät total per pääty
 * - Referees count REMAINING kyykäs (not cleared ones)
 * - Akka = kyykkä still inside the playing square (-2 points each)
 * - Pappi = kyykkä on the edge/border (-1 point each)
 * - Score = akka × -2 + pappi × -1
 * - Higher score (less negative) is better (means more kyykäs were cleared by opponent)
 */
export const KYYKKA = {
  /** Maximum kyykät per team per pääty (20 torni × 2) */
  MAX_KYYKKA: 40,
  /** Points per remaining akka (kyykkä still inside) */
  AKKA_POINTS: -2,
  /** Points per remaining pappi (kyykkä on edge) */
  PAPPI_POINTS: -1,
} as const;

/**
 * Score entry for a single pääty (half).
 */
export interface HalfScore {
  akka: number;
  pappi: number;
}

/**
 * Complete game state stored locally.
 */
export interface LocalGameState {
  id: string; // Paper ID with checksum (e.g., "1234a")
  paperId: number;
  round: number; // Round number (1 = first round, no tiebreaker allowed)
  league: string; // League ID: 'yle', 'pro', 'frm' (affects overtime rules)
  team1: JudgeTeam;
  team2: JudgeTeam;
  startTime: string;
  userId?: number; // User ID of the judge who started/finished the game
  firstHalf: {
    ready: boolean;
    team1: HalfScore;
    team2: HalfScore;
  };
  secondHalf: {
    ready: boolean;
    team1: HalfScore;
    team2: HalfScore;
  };
  overtime: {
    ready: boolean;
    team1: HalfScore;
    team2: HalfScore;
  };
  finished: boolean;
  cancelled: boolean;
}

/**
 * Calculate the score from remaining akka and pappi counts.
 * Score = akka × -2 + pappi × -1 (higher/less negative is better in kyykkä).
 * This is used both for display and backend submission.
 */
export function calculateScore(akka: number, pappi: number): number {
  return akka * KYYKKA.AKKA_POINTS + pappi * KYYKKA.PAPPI_POINTS;
}

/**
 * Calculate the display score for a single pääty.
 * Same as calculateScore - returns akka × -2 + pappi × -1.
 */
export function calculateDisplayScore(akka: number, pappi: number): number {
  return calculateScore(akka, pappi);
}

/**
 * Calculate display score from a HalfScore object.
 */
export function getHalfDisplayScore(half: HalfScore): number {
  return calculateDisplayScore(half.akka, half.pappi);
}

/**
 * Calculate total display score for a team across both päätys.
 * Returns null if not all halves are ready.
 */
export function calculateGameDisplayScore(
  game: LocalGameState,
  team: 'team1' | 'team2'
): number | null {
  // If overtime was played, use overtime score only
  if (game.overtime.ready) {
    return getHalfDisplayScore(game.overtime[team]);
  }

  // Need both halves to calculate total
  if (!game.firstHalf.ready || !game.secondHalf.ready) {
    return null;
  }

  return (
    getHalfDisplayScore(game.firstHalf[team]) +
    getHalfDisplayScore(game.secondHalf[team])
  );
}

/**
 * Calculate raw score (for backend submission) for a team across halves.
 * @deprecated Use calculateScore directly for individual halves
 */
export function calculateTotalScore(game: LocalGameState, team: 'team1' | 'team2'): number {
  const first = game.firstHalf.ready
    ? calculateScore(game.firstHalf[team].akka, game.firstHalf[team].pappi)
    : 0;
  const second = game.secondHalf.ready
    ? calculateScore(game.secondHalf[team].akka, game.secondHalf[team].pappi)
    : 0;
  
  // If overtime, use overtime score only
  if (game.overtime.ready) {
    return calculateScore(game.overtime[team].akka, game.overtime[team].pappi);
  }
  
  return first + second;
}

/**
 * Validate paper ID with checksum.
 * Paper ID format: 4 digits + 1 letter (checksum)
 * Checksum: number % 26 maps to letter (a=0, b=1, ..., z=25)
 */
export function validatePaperId(input: string): { valid: boolean; paperId: number | null } {
  // Match pattern: digits followed by single letter
  const match = input.match(/^(\d+)([a-zA-Z])$/);
  if (!match) {
    return { valid: false, paperId: null };
  }

  const number = parseInt(match[1], 10);
  const letter = match[2].toLowerCase();
  const expectedLetter = String.fromCharCode(97 + (number % 26)); // 97 = 'a'

  if (letter !== expectedLetter) {
    return { valid: false, paperId: null };
  }

  return { valid: true, paperId: number };
}

/**
 * Generate paper ID with checksum from number.
 */
export function generatePaperId(number: number): string {
  const letter = String.fromCharCode(97 + (number % 26));
  return `${number}${letter}`;
}

